'use client';

import { useEffect, useRef } from 'react';

import { useStore } from '@/providers/app-store-provider';

interface UseUserIdentityChangeOptions {
  refreshOnEnable?: boolean;
}

interface UserIdentityChangeDecisionInput {
  token: string;
  previousToken: string | null;
  hasInitialized: boolean;
  refreshOnEnable?: boolean;
}

interface UserIdentityChangeDecision {
  shouldRefresh: boolean;
  nextPreviousToken: string;
  nextHasInitialized: boolean;
}

export function getUserIdentityChangeDecision({
  token,
  previousToken,
  hasInitialized,
  refreshOnEnable = false,
}: UserIdentityChangeDecisionInput): UserIdentityChangeDecision {
  if (!hasInitialized) {
    return {
      shouldRefresh: refreshOnEnable,
      nextPreviousToken: token,
      nextHasInitialized: true,
    };
  }

  return {
    shouldRefresh: previousToken !== token,
    nextPreviousToken: token,
    nextHasInitialized: true,
  };
}

export function useUserIdentityChange(
  onChange: () => void,
  enabled = true,
  options: UseUserIdentityChangeOptions = {},
) {
  const token = useStore((state) => state.token);
  const onChangeRef = useRef(onChange);
  const previousTokenRef = useRef<string | null>(null);
  const hasInitializedRef = useRef(false);
  const refreshOnEnable = Boolean(options.refreshOnEnable);

  useEffect(() => {
    onChangeRef.current = onChange;
  }, [onChange]);

  useEffect(() => {
    if (!enabled) return;

    const decision = getUserIdentityChangeDecision({
      token,
      previousToken: previousTokenRef.current,
      hasInitialized: hasInitializedRef.current,
      refreshOnEnable,
    });

    previousTokenRef.current = decision.nextPreviousToken;
    hasInitializedRef.current = decision.nextHasInitialized;

    if (decision.shouldRefresh) {
      onChangeRef.current();
    }
  }, [enabled, refreshOnEnable, token]);
}
